import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, Button, FormInput } from "../../../components/ui";
import * as billingService from "../services/billingService";

/**
 * BankAccountModal - Add/Edit corporate bank details.
 */
export const BankAccountModal = ({ isOpen, onClose, onSuccess, initialData }) => {
    const [formData, setFormData] = useState({
        accountName: "",
        bankName: "",
        accountNumber: "",
        ifscCode: "",
        branch: "",
        isPrimary: false
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const [vendors, setVendors] = useState([]);

    useEffect(() => {
        import('../../inventory/pages/InventorySettings/services').then(services => {
            services.getInventorySettings({ settingType: "vendor", status: "ACTIVE", limit: 10000 })
                .then(res => {
                    const data = res?.data?.data || res?.data || [];
                    setVendors(Array.isArray(data) ? data : []);
                })
                .catch(err => console.error("Failed to load vendors", err));
        });
    }, []);

    useEffect(() => {
        setErrors({});
        if (initialData) {
            setFormData({
                vendor: initialData.vendor || "",
                accountName: initialData.accountName || "",
                bankName: initialData.bankName || "",
                accountNumber: initialData.accountNumber || "",
                ifscCode: initialData.ifscCode || "",
                branch: initialData.branch || "",
                upiId: initialData.upiId || "",
                isPrimary: initialData.isPrimary || false
            });
        } else {
            setFormData({
                vendor: "",
                accountName: "",
                bankName: "",
                accountNumber: "",
                ifscCode: "",
                branch: "",
                upiId: "",
                isPrimary: false
            });
        }
    }, [initialData, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setErrors({});

            const submitData = { ...formData };
            if (!submitData.vendor) {
                delete submitData.vendor;
            }

            if (initialData?._id) {
                await billingService.updateBank(initialData._id, submitData);
                toast.success("Bank account updated!");
            } else {
                await billingService.createBank(submitData);
                toast.success("Bank account added!");
            }
            onSuccess();
            onClose();
        } catch (err) {
            if (err.response?.data?.errors) {
                setErrors(err.response.data.errors);
            }
            toast.error(err.response?.data?.message || "Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <div className="flex gap-3 w-full">
            <Button variant="secondary" onClick={onClose} fullWidth className="rounded-lg font-bold">
                Cancel
            </Button>
            <Button 
                type="button" 
                variant="orange" 
                loading={isSubmitting} 
                onClick={handleSubmit}
                fullWidth 
                className="rounded-lg font-bold shadow-lg shadow-orange-100/50"
            >
                Save Bank Details
            </Button>
        </div>
    );

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title={initialData ? "Edit Bank Account" : "Add Bank Account"}
            size="md"
            footer={footer}
        >
            <form onSubmit={handleSubmit} className="space-y-3">
                <div className="flex flex-col gap-1">
                    <label className="text-[13px] font-semibold text-slate-700">Vendor</label>
                    <select
                        name="vendor"
                        value={typeof formData.vendor === 'object' ? formData.vendor?._id || '' : formData.vendor}
                        onChange={handleChange}
                        className="w-full h-10 px-3 py-2 text-[13px] bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue/20 transition-all text-slate-700 font-medium"
                    >
                        <option value="">Select Vendor (Optional)</option>
                        {vendors.map(v => (
                            <option key={v._id} value={v._id}>
                                {v.vendorName || v.name || v.title || v._id}
                            </option>
                        ))}
                    </select>
                </div>

                <FormInput 
                    label="Account Holder Name" 
                    field="accountName" 
                    value={formData.accountName} 
                    onChange={handleChange} 
                    error={errors.accountName}
                    placeholder="e.g. Verve Kitchens Private Limited"
                    required 
                />

                <FormInput 
                    label="Account Number" 
                    field="accountNumber" 
                    value={formData.accountNumber} 
                    onChange={handleChange} 
                    error={errors.accountNumber}
                    placeholder="e.g. 501002345678"
                    required 
                />

                <div className="grid grid-cols-2 gap-4">
                    <FormInput 
                        label="Bank Name" 
                        field="bankName" 
                        value={formData.bankName} 
                        onChange={handleChange} 
                        error={errors.bankName}
                        placeholder="e.g. HDFC Bank"
                        required 
                    />
                    <FormInput 
                        label="IFSC Code" 
                        field="ifscCode" 
                        value={formData.ifscCode} 
                        onChange={handleChange} 
                        error={errors.ifscCode}
                        placeholder="e.g. HDFC0001234"
                        required 
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <FormInput 
                        label="Branch Name" 
                        field="branch" 
                        value={formData.branch} 
                        onChange={handleChange} 
                        error={errors.branch}
                        placeholder="e.g. MG Road Branch"
                    />

                    <FormInput 
                        label="UPI ID / Merchant ID" 
                        field="upiId" 
                        value={formData.upiId} 
                        onChange={handleChange} 
                        error={errors.upiId}
                        placeholder="e.g. merchant@upi"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <input 
                        type="checkbox" 
                        id="isPrimaryBank"
                        name="isPrimary" 
                        checked={formData.isPrimary} 
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue/20"
                    />
                    <label htmlFor="isPrimaryBank" className="text-[13px] font-semibold text-slate-600 cursor-pointer">
                        Set as Primary Account
                    </label>
                </div>
            </form>
        </Modal>
    );
};
